/**
 * Product detail sub-components — ported from AJS directives.
 *
 * product-invoices        → wor-table backed by /products/invoice_list
 * product-stock-orders    → wor-table backed by /products/purchase_order_list
 * product-history         → wor-table backed by /products/vendor_invoice_list
 * product-stock-take      → wor-table backed by /stock_take_logs
 * product-reserved        → wor-table backed by /products/reserved_invoice_list
 * product-serial-numbers  → inline CRUD grid for serial numbers
 * product-stock-transfers → simple inline table (no pagination)
 */
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { ApiService }           from '../api/api.service';
import { CompanyService }       from '../companies/company.service';
import { MessagesService }      from '../messages/messages.service';
import { ProductModel }         from './models/product';
import { TableQueryParameters } from '../table/types/table-query-parameters';
import { TableRowEvent }        from '../table/types/table-row-event';
import { TableService }         from '../table/table.service';
import { EditComponent }        from '../table/cell-components/actions/edit.component';

// ---------------------------------------------------------------------------
// product-invoices
// ---------------------------------------------------------------------------
@Component({
  selector: 'product-invoices',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-folder-open"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.PRODUCT_INVOICES' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="invoices"
          (query)="getInvoices($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class ProductInvoicesPlaceholderComponent implements OnInit {
  @Input() product: ProductModel;
  @Input() invoices: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.invoices?.length;
    const cache = this.tableService.getCacheForTable('productInvoices');

    this.tableSettings = {
      cache: { enabled: true, key: 'productInvoices' },
      columns: [
        { field: 'invoice_number', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.REFERENCE') } },
        { field: 'display_name',   header: { text: this.translate.instant('CUSTOMER_SPACE.CUSTOMER') } },
        { field: 'post_date',      header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'invoice_type',   header: { text: this.translate.instant('JS_SPACE.COLUMNS.TRAN_TYPE') }, pipe: 'invoiceType' },
        { field: 'invoice_status', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'invoiceStatus' },
        { field: 'quantity',       header: { text: this.translate.instant('PRODUCT_SPACE.QTY') } },
        { field: 'amount',         header: { text: this.translate.instant('JS_SPACE.COLUMNS.AMOUNT') }, pipe: 'globalCurrency' },
        { component: { bindings: { outputs: { onEditClick: (e: Record<string, unknown>) => this.viewInvoice(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/products/invoice_count/' + this.product.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'post_date' } };
  }

  getInvoices(params: TableQueryParameters) {
    return this.api.get('/products/invoice_list', [this.product.id, params.offset, params.pageSize, params.field, params.direction])
      .then((invoices: Record<string, unknown>[]) => { this.invoices = invoices; return invoices; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewInvoice(event.row);
  }

  viewInvoice(invoice: Record<string, unknown>) {
    if (invoice.contains_vehicle_sale) {
      this.router.stateService.go('app.dealership.sale', { customer_id: invoice.customer_id, id: invoice.id });
    } else {
      this.router.stateService.go('app.invoice', { customer_id: invoice.customer_id, id: invoice.id, vehicle_id: invoice.vehicle_id });
    }
  }
}

// ---------------------------------------------------------------------------
// product-stock-orders
// ---------------------------------------------------------------------------
@Component({
  selector: 'product-stock-orders',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-list-ul"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.STOCK_ORDERS' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="orders"
          (query)="getOrders($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class ProductStockOrdersPlaceholderComponent implements OnInit {
  @Input() product: ProductModel;
  @Input() orders: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.orders?.length;
    const cache = this.tableService.getCacheForTable('productStockOrders');

    this.tableSettings = {
      cache: { enabled: true, key: 'productStockOrders' },
      columns: [
        { field: 'order_number',  header: { text: this.translate.instant('PURCHASE_ORDER_SPACE.ORDER_NUMBER') } },
        { field: 'company_name',  header: { text: this.translate.instant('VENDOR_SPACE.SUPPLIER') } },
        { field: 'order_date',    header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'due_date',      header: { text: this.translate.instant('PURCHASE_ORDER_SPACE.DUE_DATE') }, pipe: 'globalDate' },
        { field: 'note',          header: { text: this.translate.instant('INSPECTIONS_SPACE.COMMENT') } },
        { field: 'status',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'purchaseOrderStatus' },
        { field: 'total',         header: { text: this.translate.instant('GENERAL_SPACE.FIELD.TOTAL'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: Record<string, unknown>) => this.viewOrder(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_STOCK_ORDERS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/products/purchase_order_count/' + this.product.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'order_date' } };
  }

  getOrders(params: TableQueryParameters) {
    return this.api.get('/products/purchase_order_list', [this.product.id, params.offset, params.pageSize, params.field, params.direction])
      .then((orders: Record<string, unknown>[]) => { this.orders = orders; return orders; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewOrder(event.row);
  }

  viewOrder(order: Record<string, unknown>) {
    this.router.stateService.go('app.purchase-order', { vendor_id: order.vendor_id, id: order.id });
  }
}

// ---------------------------------------------------------------------------
// product-history
// ---------------------------------------------------------------------------
@Component({
  selector: 'product-history',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-bar-chart-o"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.PURCHASE_HISTORY' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="vendorInvoices"
          (query)="getVendorInvoices($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class ProductHistoryPlaceholderComponent implements OnInit {
  @Input() product: ProductModel;
  @Input() vendorInvoices: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.vendorInvoices?.length;
    const cache = this.tableService.getCacheForTable('productHistory');

    this.tableSettings = {
      cache: { enabled: true, key: 'productHistory' },
      columns: [
        { field: 'vendor_invoice_number', header: { text: this.translate.instant('GENERAL_SPACE.FIELD.REFERENCE') } },
        { field: 'post_date',             header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'company_name',          header: { text: this.translate.instant('VENDOR_SPACE.SUPPLIER') } },
        { field: 'quantity',              header: { text: this.translate.instant('PRODUCT_SPACE.QTY'), classes: 'text-right' }, pipe: 'number', classes: 'text-right' },
        { field: 'total',                 header: { text: this.translate.instant('GENERAL_SPACE.FIELD.TOTAL'), classes: 'text-right' }, pipe: 'globalCurrency', classes: 'text-right' },
        { field: 'invoice_status',        header: { text: this.translate.instant('GENERAL_SPACE.FIELD.STATUS'), classes: 'text-right' }, pipe: 'invoiceStatus', classes: 'text-right' },
        { component: { bindings: { outputs: { onEditClick: (e: Record<string, unknown>) => this.viewVendorInvoice(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_PURCHASE_HISTORY_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/products/vendor_invoice_count/' + this.product.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'asc', field: cache.sorting?.field || 'post_date' } };
  }

  getVendorInvoices(params: TableQueryParameters) {
    return this.api.get('/products/vendor_invoice_list', [this.product.id, params.offset, params.pageSize, params.field, params.direction])
      .then((vendorInvoices: Record<string, unknown>[]) => { this.vendorInvoices = vendorInvoices; return vendorInvoices; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewVendorInvoice(event.row);
  }

  viewVendorInvoice(invoice: Record<string, unknown>) {
    this.router.stateService.go('app.vendor-invoice', { vendor_id: invoice.vendor_id, id: invoice.id });
  }
}

// ---------------------------------------------------------------------------
// product-stock-take
// ---------------------------------------------------------------------------
@Component({
  selector: 'product-stock-take',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa flaticon-inventory"></i><span class="break"></span>{{ 'QUERY_DROPDOWN_SPACE.NAMES.STOCK_TAKE_LOG' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="log"
          (query)="getLog($event.params)"
          [settings]="tableSettings">
        </wor-table>
      </div>
    </div>
  ` })
export class ProductStockTakePlaceholderComponent implements OnInit {
  @Input() product: ProductModel;
  @Input() log: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.log?.length;
    const cache = this.tableService.getCacheForTable('productStockTake');

    this.tableSettings = {
      cache: { enabled: true, key: 'productStockTake' },
      columns: [
        { field: 'post_date',     header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'old_quantity',  header: { text: this.translate.instant('JS_SPACE.COLUMNS.OLD_QUANTITY') } },
        { field: 'new_quantity',  header: { text: this.translate.instant('JS_SPACE.COLUMNS.NEW_QUANTITY') } },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_STOCK_TAKE_LOG_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/stock_take_logs/count/' + this.product.id) },
      rows: { highlight: { enabled: true } },
      sorting: { enabled: false } };
  }

  getLog(params: TableQueryParameters) {
    return this.api.get('/stock_take_logs', [this.product.id, params.offset, params.pageSize])
      .then((log: Record<string, unknown>[]) => { this.log = log; return log; });
  }
}

// ---------------------------------------------------------------------------
// product-reserved
// ---------------------------------------------------------------------------
@Component({
  selector: 'product-reserved',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-folder-open"></i><span class="break"></span>{{ 'PRODUCT_SPACE.PRODUCT_RESERVED' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <wor-table
          [(data)]="reserved"
          (query)="getReserved($event.params)"
          [settings]="tableSettings"
          (rowDoubleClick)="onRowDoubleClick($event)">
        </wor-table>
      </div>
    </div>
  ` })
export class ProductReservedPlaceholderComponent implements OnInit {
  @Input() product: ProductModel;
  @Input() reserved: Record<string, unknown>[];

  collapsed = false;
  tableSettings: Record<string, unknown>;

  constructor(
    private api: ApiService,
    private router: UIRouter,
    private tableService: TableService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.collapsed = !this.reserved?.length;
    const cache = this.tableService.getCacheForTable('productReserved');

    this.tableSettings = {
      cache: { enabled: true, key: 'productReserved' },
      columns: [
        { field: 'job_card_number', header: { text: this.translate.instant('INVOICE_SPACE.JOB_CARD_NUMBER') } },
        { field: 'post_date',       header: { text: this.translate.instant('GENERAL_SPACE.FIELD.POST_DATE') }, pipe: 'globalDate' },
        { field: 'invoice_type',    header: { text: this.translate.instant('JS_SPACE.COLUMNS.TRAN_TYPE') }, pipe: 'invoiceType' },
        { field: 'quantity',        header: { text: this.translate.instant('PRODUCT_SPACE.QTY') } },
        { field: 'amount',          header: { text: this.translate.instant('JS_SPACE.COLUMNS.AMOUNT') }, pipe: 'globalCurrency' },
        { component: { bindings: { outputs: { onEditClick: (e: Record<string, unknown>) => this.viewInvoice(e) } }, enabled: true, factory: EditComponent }, sortable: false },
      ],
      empty: { text: this.translate.instant('QUERY_DROPDOWN_SPACE.GRID.NO_RESERVED_FOUND') },
      pagination: {
        enabled: true,
        pageSize: cache.paging?.pageSize || 5,
        pageSizes: [5, 10, 25, 50],
        queryCount: () => this.api.get('/products/reserved_invoice_count/' + this.product.id) },
      rows: { highlight: { enabled: true } },
      sorting: { direction: cache.sorting?.direction || 'desc', field: cache.sorting?.field || 'post_date' } };
  }

  getReserved(params: TableQueryParameters) {
    return this.api.get('/products/reserved_invoice_list', [this.product.id, params.offset, params.pageSize, params.field, params.direction])
      .then((reserved: Record<string, unknown>[]) => { this.reserved = reserved; return reserved; });
  }

  onRowDoubleClick(event: TableRowEvent) {
    this.viewInvoice(event.row);
  }

  viewInvoice(invoice: Record<string, unknown>) {
    if (invoice.contains_vehicle_sale) {
      this.router.stateService.go('app.dealership.sale', { customer_id: invoice.customer_id, id: invoice.id });
    } else {
      this.router.stateService.go('app.invoice', { customer_id: invoice.customer_id, id: invoice.id, vehicle_id: invoice.vehicle_id });
    }
  }
}

// ---------------------------------------------------------------------------
// product-serial-numbers — inline CRUD grid (not wor-table)
// ---------------------------------------------------------------------------
@Component({
  selector: 'product-serial-numbers',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-dollar"></i><span class="break"></span>{{ 'SERIAL_NUMBER_SPACE.SERIAL_NUMBERS' | translate }}</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <table class="table table-striped">
          <thead>
            <tr>
              <th></th>
              <th>{{ 'SERIAL_NUMBER_SPACE.SERIAL_NUMBER' | translate }}</th>
              <th>{{ 'GENERAL_SPACE.FIELD.STATUS' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let sn of serialNumbers; let i = index">
              <td>
                <button class="btn btn-xs btn-danger" *ngIf="sn.status !== 'sold'" (click)="deleteSerialNumber(sn, i)">
                  <i class="fa fa-trash"></i>
                </button>
              </td>
              <td>
                <span *ngIf="editingIndex !== i">{{ sn.serial_number }}</span>
                <input *ngIf="editingIndex === i" type="text" class="form-control input-sm" [(ngModel)]="editValue" />
              </td>
              <td>{{ sn.status }}</td>
              <td>
                <button class="btn btn-xs btn-default" *ngIf="editingIndex !== i && sn.status !== 'sold'" (click)="startEdit(i)">
                  <i class="fa fa-pencil"></i>
                </button>
                <button class="btn btn-xs btn-success" *ngIf="editingIndex === i" (click)="saveSerialNumber(sn)">
                  <i class="fa fa-check"></i>
                </button>
                <button class="btn btn-xs btn-default" *ngIf="editingIndex === i" (click)="cancelEdit()">
                  <i class="fa fa-times"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="form-inline m-t-sm">
          <input type="text" class="form-control input-sm" [(ngModel)]="newSerialNumber" [placeholder]="'SERIAL_NUMBER_SPACE.SERIAL_NUMBER' | translate" />
          <button class="btn btn-sm btn-primary" (click)="addSerialNumber()">
            <i class="fa fa-plus"></i> {{ 'GENERAL_SPACE.BUTTON.ADD' | translate }}
          </button>
        </div>
      </div>
    </div>
  ` })
export class ProductSerialNumbersPlaceholderComponent {
  @Input() product: ProductModel;
  @Input() serialNumbers: Record<string, unknown>[];

  collapsed = false;
  editingIndex: number = null;
  editValue = '';
  newSerialNumber = '';

  constructor(
    private api: ApiService,
    private messages: MessagesService,
  ) {}

  addSerialNumber() {
    if (!this.newSerialNumber) return;
    this.api.post('/product_serial_numbers', { product_id: this.product.id, serial_number: this.newSerialNumber })
      .then((sn: Record<string, unknown>) => {
        this.serialNumbers.push(sn);
        this.newSerialNumber = '';
      })
      .catch(err => this.messages.error(err));
  }

  startEdit(index: number) {
    this.editingIndex = index;
    this.editValue = this.serialNumbers[index].serial_number as string;
  }

  cancelEdit() {
    this.editingIndex = null;
    this.editValue = '';
  }

  saveSerialNumber(sn: Record<string, unknown>) {
    this.api.patch('/product_serial_number', { product_id: this.product.id, serial_number: this.editValue, id: sn.id })
      .then((updated: Record<string, unknown>) => {
        sn.serial_number = updated.serial_number;
        this.editingIndex = null;
        this.editValue = '';
      })
      .catch(err => this.messages.error(err));
  }

  deleteSerialNumber(sn: Record<string, unknown>, index: number) {
    this.api.delete('/product_serial_numbers/delete_from_product_list', String(sn.id))
      .then(() => { this.serialNumbers.splice(index, 1); })
      .catch(err => this.messages.error(err));
  }
}

// ---------------------------------------------------------------------------
// product-stock-transfers — simple inline table (no pagination)
// ---------------------------------------------------------------------------
@Component({
  selector: 'product-stock-transfers',
  template: `
    <div class="box">
      <div class="box-header">
        <h2><i class="fa fa-exchange"></i><span class="break"></span>Stock Transfers</h2>
        <div class="box-icon">
          <a href="javascript:void(0)" (click)="collapsed = !collapsed"><i class="fa" [ngClass]="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i></a>
        </div>
      </div>
      <div class="box-content" [hidden]="collapsed">
        <table class="table table-striped" *ngIf="transfers?.length; else emptyBlock">
          <thead>
            <tr>
              <th class="text-left">Description</th>
              <th class="text-left">Created On</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let transfer of transfers">
              <td class="text-left">{{ transfer.description }}</td>
              <td class="text-left">{{ transfer.created_on | globalDate }}</td>
            </tr>
          </tbody>
        </table>
        <ng-template #emptyBlock>
          <p>No Stock Transfers</p>
        </ng-template>
      </div>
    </div>
  ` })
export class ProductStockTransfersPlaceholderComponent {
  @Input() product: ProductModel;
  @Input() transfers: Record<string, unknown>[];

  collapsed = false;

  ngOnInit() {
    this.collapsed = !this.transfers?.length;
  }
}
